import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { Button, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";

import FullPageLoader from "../../../common/components/item/FullPageLoader";
import MainPaperWrapper from "../../../common/components/item/MainPaperWrapper";
import LiveViewContainer, {
  ViewMode,
  ViewModeEnum,
} from "../../../common/components/live-view/LiveViewContainer";
import { captureLiveImage } from "../../../common/helpers/captureLiveImage";
import { useStreamProcesses } from "../../../common/hooks/useStreamProcesses";
import useWindowDimensions from "../../../common/hooks/useWindowDimensions";
import { useGetZonesByDevice } from "../../data-hub/components/liveview/components/zonelist/hooks/useGetZonesByDevice";
import { ServiceTypeEnum } from "../../model-manager/variables/modelManager";
import DeviceDetailPageHeader from "../components/DeviceDetailPageHeader";
import DeviceZones from "../components/DeviceZones";
import { IDeviceTableRow } from "../hooks/useDeviceTableRows";
import useGetDeviceByUrl from "../hooks/useGetDeviceByUrl";
import { usePublishNode } from "../hooks/usePublishNode";
import { deviceDataVariable, useDeviceDataState } from "../variables/devices";
import { useListenToZoneUpdates } from "../zones-setup-page/hooks/useListenToZoneChanges";
import DeviceDetailViewMode from "./components/DeviceDetailViewMode";
import DeviceModelsContainer from "./components/DeviceModelsContainer";

const DeviceDetailPage = () => {
  const theme = useTheme();
  const { width } = useWindowDimensions();

  const { publishNode } = usePublishNode();

  const { deviceId: deviceIdSearchParameter = "" } = useParams();

  const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(
    ViewModeEnum.KEYFRAME
  );

  const memoizedSetCurrentViewModeCallback = useCallback((mode: ViewMode) => {
    setCurrentViewMode(mode);
  }, []);

  const { fetchDevice, getServicesLoading, loading, isServiceLoaded } =
    useGetDeviceByUrl();

  const { data, loading: getZonesByDeviceLoading } = useGetZonesByDevice();

  const zones = data?.getZonesByDevice.items ?? [];

  const device = useDeviceDataState();

  useListenToZoneUpdates();

  const { streamLoading, stopStreaming } = useStreamProcesses();

  useEffect((): void => {
    if (!isServiceLoaded || !deviceIdSearchParameter || !fetchDevice) return;

    const loadDevice = async () => {
      const result = await fetchDevice();

      if (result) {
        deviceDataVariable(result as IDeviceTableRow);
      }
    };

    loadDevice();
  }, [deviceIdSearchParameter, isServiceLoaded, fetchDevice]);

  useEffect(() => {
    return () => {
      deviceDataVariable(null);

      if (device) {
        stopStreaming(device);
      }
    };
  }, []);

  const handleCaptureRecentFrame = (): void => {
    if (device) {
      captureLiveImage(device, publishNode);
    }
  };

  if (
    !device &&
    (streamLoading || getServicesLoading || loading || getZonesByDeviceLoading)
  ) {
    return <FullPageLoader />;
  }

  if (!device) {
    return null;
  }

  const hasGasLeakServiceBeenCreated = device.models.some(
    (el): boolean =>
      el.serviceName === ServiceTypeEnum.GAS_LEAK ||
      el.serviceName === ServiceTypeEnum.LEAK ||
      el.serviceName === ServiceTypeEnum.LIQUID_LEAK
  );

  const showZonesSection = device.deviceData.hasPanTilt;

  const zonesPrecondition = hasGasLeakServiceBeenCreated;

  const tooltipTitle =
    "Will send a request to capture a live image from the camera.";

  const isCaptureKeyFramesDisabled =
    !device?.isOnline ||
    !device?.deviceData.recentFrame ||
    currentViewMode === ViewModeEnum.LIVE;

  return (
    <Box
      sx={{
        display: "grid",
        gap: "1.5em",
      }}
    >
      <DeviceDetailPageHeader deviceDetails={device} />
      <MainPaperWrapper>
        <Box
          sx={{
            "& > .MuiBox-root": {
              maxWidth:
                width > theme.breakpoints.values.xxl
                  ? theme.breakpoints.values.sm
                  : "100%",
            },
            "@media (min-width: 1440px)": {
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
            },
          }}
        >
          <DeviceDetailViewMode deviceDatum={device} />

          <Box>
            {!device?.deviceData.hasPanTilt && (
              <Tooltip title={tooltipTitle} placement="top">
                <Box
                  sx={{
                    marginRight: "0.5em",
                    marginBottom: "0.5em",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    disabled={isCaptureKeyFramesDisabled}
                    variant="text"
                    size="small"
                    startIcon={<PhotoCameraOutlinedIcon />}
                    color="inherit"
                    onClick={handleCaptureRecentFrame}
                    sx={{
                      "&.Mui-disabled": {
                        backgroundColor: "transparent",
                        color: theme.palette.text.disabled,
                      },
                      ":hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Capture Live Image
                  </Button>
                </Box>
              </Tooltip>
            )}
            <LiveViewContainer
              device={device}
              showPtButton={device?.deviceData.hasPanTilt}
              s3Key={device?.deviceData.signedUrlRecentFrame ?? ""}
              viewMode={currentViewMode}
              setCurrentViewMode={memoizedSetCurrentViewModeCallback}
            />
          </Box>
        </Box>
      </MainPaperWrapper>

      <DeviceModelsContainer device={device} />

      {showZonesSection && (
        <DeviceZones
          device={device}
          hasPanTilt={device?.deviceData.hasPanTilt}
          currentViewMode={currentViewMode}
          zones={zones}
          loading={getZonesByDeviceLoading}
          precondition={zonesPrecondition}
        />
      )}
    </Box>
  );
};

export default DeviceDetailPage;
